import React from 'react';
import {
  Box,
  Paper,
  Typography,
  Avatar,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import ReactMarkdown from 'react-markdown';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneDark } from 'react-syntax-highlighter/dist/esm/styles/prism';
import PersonIcon from '@mui/icons-material/Person';
import { formatTime } from '../utils/formatters';

function MessageBubble({ isTyping, message, userPhoto }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isUser = message.sender === 'user';

  const markdownComponents = {
    code({ node, inline, className, children, ...props }) {
      const match = /language-(\w+)/.exec(className || '');
      const language = match ? match[1] : 'text';

      return !inline ? (
        <SyntaxHighlighter
          style={oneDark}
          language={language}
          PreTag="div"
          customStyle={{
            margin: '1em 0',
            borderRadius: '8px',
            fontSize: isMobile ? '0.75rem' : '0.875rem',
          }}
          {...props}
        >
          {String(children).replace(/\n$/, '')}
        </SyntaxHighlighter>
      ) : (
        <Box
          component="code"
          sx={{
            backgroundColor: 'rgba(255,255,255,0.1)',
            padding: '2px 6px',
            borderRadius: '4px',
            fontFamily: 'monospace',
            fontSize: '0.875em',
          }}
          {...props}
        >
          {children}
        </Box>
      );
    },

    a({ href, children }) {
      return (
        <Box
          component="a"
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          sx={{
            color: '#90caf9',
            textDecoration: 'underline',
            '&:hover': {
              color: '#bbdefb',
            },
          }}
        >
          {children}
        </Box>
      );
    },

    ul({ children }) {
      return (
        <Box component="ul" sx={{ margin: '0.5em 0', paddingLeft: '1.5em' }}>
          {children}
        </Box>
      );
    },

    ol({ children }) {
      return (
        <Box component="ol" sx={{ margin: '0.5em 0', paddingLeft: '1.5em' }}>
          {children}
        </Box>
      );
    },

    p({ children }) {
      return (
        <Typography
          variant="body1"
          component="p"
          sx={{
            margin: '0.5em 0',
            fontSize: isMobile ? '0.875rem' : '1rem',
            lineHeight: isMobile ? 1.4 : 1.5,
            '&:first-of-type': { marginTop: 0 },
            '&:last-of-type': { marginBottom: 0 },
          }}
        >
          {children}
        </Typography>
      );
    },

    h1: ({ children }) => (
      <Typography
        variant="h5"
        component="h1"
        sx={{ margin: '1em 0 0.5em 0', fontWeight: 'bold' }}
      >
        {children}
      </Typography>
    ),
    h2: ({ children }) => (
      <Typography
        variant="h6"
        component="h2"
        sx={{ margin: '1em 0 0.5em 0', fontWeight: 'bold' }}
      >
        {children}
      </Typography>
    ),
    h3: ({ children }) => (
      <Typography
        variant="subtitle1"
        component="h3"
        sx={{ margin: '1em 0 0.5em 0', fontWeight: 'bold' }}
      >
        {children}
      </Typography>
    ),

    blockquote({ children }) {
      return (
        <Box
          sx={{
            borderLeft: '4px solid rgba(255,255,255,0.3)',
            paddingLeft: '1em',
            margin: '1em 0',
            fontStyle: 'italic',
            opacity: 0.9,
          }}
        >
          {children}
        </Box>
      );
    },

    table({ children }) {
      return (
        <Box
          component="table"
          sx={{
            width: '100%',
            borderCollapse: 'collapse',
            margin: '1em 0',
            '& th, & td': {
              border: '1px solid rgba(255,255,255,0.2)',
              padding: '8px 12px',
              textAlign: 'left',
            },
            '& th': {
              backgroundColor: 'rgba(255,255,255,0.1)',
              fontWeight: 'bold',
            },
          }}
        >
          {children}
        </Box>
      );
    },
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: isUser ? 'row-reverse' : 'row',
        alignItems: 'flex-start',
        maxWidth: isMobile ? '95%' : '80%',
        alignSelf: isUser ? 'flex-end' : 'flex-start',
      }}
    >
      <Avatar
        src={isUser ? <PersonIcon /> : userPhoto}
        sx={{
          bgcolor: isUser ? '#1976D2' : '#37474F',
          width: isMobile ? 32 : 40,
          height: isMobile ? 32 : 40,
        }}
      />
      <Box
        sx={{
          mx: isMobile ? 1 : 2,
          maxWidth: isMobile ? 'calc(100% - 50px)' : 'calc(100% - 90px)',
        }}
      >
        <Paper
          elevation={1}
          sx={{
            p: isMobile ? 1.5 : 2,
            borderRadius: isMobile ? 1.5 : 2,
            bgcolor: isUser ? '#1976D2' : '#37474F',
            color: 'white',
            position: 'relative',
          }}
        >
          {isUser ? (
            <Typography
              variant="body1"
              sx={{
                whiteSpace: 'pre-wrap',
                fontSize: isMobile ? '0.875rem' : '1rem',
                lineHeight: isMobile ? 1.4 : 1.5,
              }}
            >
              {message.text}
            </Typography>
          ) : (
            <ReactMarkdown components={markdownComponents}>
              {message.text}
            </ReactMarkdown>
          )}

          {message.isStreaming && (
            <Box
              component="span"
              sx={{
                display: 'inline-block',
                width: '2px',
                height: '1em',
                bgcolor: 'primary.main',
                ml: 0.5,
                animation: 'blink 1s infinite',
                '@keyframes blink': {
                  '0%, 50%': { opacity: 1 },
                  '51%, 100%': { opacity: 0 },
                },
              }}
            />
          )}
        </Paper>
        <Box
          sx={{
            mt: 0.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: isUser ? 'flex-end' : 'flex-start',
          }}
        >
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ fontSize: isMobile ? '0.65rem' : '0.75rem' }}
          >
            {!isUser &&
              (message.isStreaming
                ? 'Assistant • Typing...'
                : `Assistant • ${formatTime(message.timestamp)}`)}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}

export default MessageBubble;
